import React from 'react';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
}

export const Card: React.FC<CardProps> = ({
  children,
  className = '',
  padding = 'md',
  hover = false,
}) => {
  const paddingStyles = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  return (
    <div data-component-id="src/components/ui/Card.tsx:24:4" data-component-path="src/components/ui/Card.tsx" data-component-line="24" data-component-end-line="33" data-component-file="Card.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      className={`
        bg-card rounded-xl border border-border shadow-sm
        ${paddingStyles[padding]}
        ${hover ? 'hover:shadow-md hover:border-primary/20 transition-all duration-200' : ''}
        ${className}
      `}
    >
      {children}
    </div>
  );
};

interface StatsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  accentColor?: 'primary' | 'secondary' | 'success' | 'warning' | 'destructive' | 'accent';
  className?: string;
}

const accentColorMap: Record<string, { stripe: string; iconBg: string; iconText: string }> = {
  primary: { stripe: 'bg-primary', iconBg: 'bg-primary/5', iconText: 'text-primary' },
  secondary: { stripe: 'bg-secondary', iconBg: 'bg-secondary/5', iconText: 'text-secondary' },
  success: { stripe: 'bg-success', iconBg: 'bg-success/5', iconText: 'text-success' },
  warning: { stripe: 'bg-warning', iconBg: 'bg-warning/5', iconText: 'text-warning' },
  destructive: { stripe: 'bg-destructive', iconBg: 'bg-destructive/5', iconText: 'text-destructive' },
  accent: { stripe: 'bg-accent', iconBg: 'bg-accent/5', iconText: 'text-accent' },
};

export const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  accentColor,
  className = '',
}) => {
  const colors = accentColor ? accentColorMap[accentColor] : null;

  return (
    <Card data-component-id="src/components/ui/Card.tsx:71:4" data-component-path="src/components/ui/Card.tsx" data-component-line="71" data-component-end-line="102" data-component-file="Card.tsx" data-component-name="Card" data-component-content="%7B%22elementName%22%3A%22Card%22%7D" className={`overflow-hidden ${className}`} padding="none" hover>
      {colors && <div data-component-id="src/components/ui/Card.tsx:72:17" data-component-path="src/components/ui/Card.tsx" data-component-line="72" data-component-end-line="72" data-component-file="Card.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`h-1 ${colors.stripe}`} />}
      <div data-component-id="src/components/ui/Card.tsx:73:6" data-component-path="src/components/ui/Card.tsx" data-component-line="73" data-component-end-line="101" data-component-file="Card.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`${colors ? 'p-5' : 'p-6'}`}>
        <div data-component-id="src/components/ui/Card.tsx:74:8" data-component-path="src/components/ui/Card.tsx" data-component-line="74" data-component-end-line="100" data-component-file="Card.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20justify-between%22%7D" className="flex items-start justify-between">
          <div data-component-id="src/components/ui/Card.tsx:75:10" data-component-path="src/components/ui/Card.tsx" data-component-line="75" data-component-end-line="94" data-component-file="Card.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1">
            <p data-component-id="src/components/ui/Card.tsx:76:12" data-component-path="src/components/ui/Card.tsx" data-component-line="76" data-component-end-line="76" data-component-file="Card.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20font-medium%20text-muted-foreground%22%7D" className="text-sm font-medium text-muted-foreground">{title}</p>
            <p data-component-id="src/components/ui/Card.tsx:77:12" data-component-path="src/components/ui/Card.tsx" data-component-line="77" data-component-end-line="77" data-component-file="Card.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-2%20text-3xl%20font-bold%20text-foreground%22%7D" className="mt-2 text-3xl font-bold text-foreground">{value}</p>
            {subtitle && (
              <p data-component-id="src/components/ui/Card.tsx:79:14" data-component-path="src/components/ui/Card.tsx" data-component-line="79" data-component-end-line="79" data-component-file="Card.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-muted-foreground%22%7D" className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
            )}
            {trend && (
              <div data-component-id="src/components/ui/Card.tsx:82:14" data-component-path="src/components/ui/Card.tsx" data-component-line="82" data-component-end-line="92" data-component-file="Card.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-2%20flex%20items-center%20gap-1%22%7D" className="mt-2 flex items-center gap-1">
                <span data-component-id="src/components/ui/Card.tsx:83:16" data-component-path="src/components/ui/Card.tsx" data-component-line="83" data-component-end-line="90" data-component-file="Card.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22%25%22%7D"
                  className={`text-sm font-medium ${
                    trend.isPositive ? 'text-success' : 'text-destructive'
                  }`}
                >
                  {trend.isPositive ? '+' : ''}
                  {trend.value}%
                </span>
                <span data-component-id="src/components/ui/Card.tsx:91:16" data-component-path="src/components/ui/Card.tsx" data-component-line="91" data-component-end-line="91" data-component-file="Card.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%22%2C%22textContent%22%3A%22vs%20last%20month%22%7D" className="text-xs text-muted-foreground">vs last month</span>
              </div>
            )}
          </div>
          {icon && (
            <div data-component-id="src/components/ui/Card.tsx:96:12" data-component-path="src/components/ui/Card.tsx" data-component-line="96" data-component-end-line="98" data-component-file="Card.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`flex-shrink-0 p-3 rounded-2xl ${colors ? colors.iconBg : 'bg-primary/5'} ${colors ? colors.iconText : 'text-primary'}`}>
              {icon}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

interface ContentCardProps {
  title: string;
  description?: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

export const ContentCard: React.FC<ContentCardProps> = ({
  title,
  description,
  children,
  action,
  className = '',
}) => {
  return (
    <Card data-component-id="src/components/ui/Card.tsx:122:4" data-component-path="src/components/ui/Card.tsx" data-component-line="122" data-component-end-line="133" data-component-file="Card.tsx" data-component-name="Card" data-component-content="%7B%22elementName%22%3A%22Card%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className}>
      <div data-component-id="src/components/ui/Card.tsx:123:6" data-component-path="src/components/ui/Card.tsx" data-component-line="123" data-component-end-line="131" data-component-file="Card.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-4%22%7D" className="flex items-center justify-between mb-4">
        <div data-component-id="src/components/ui/Card.tsx:124:8" data-component-path="src/components/ui/Card.tsx" data-component-line="124" data-component-end-line="129" data-component-file="Card.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <h3 data-component-id="src/components/ui/Card.tsx:125:10" data-component-path="src/components/ui/Card.tsx" data-component-line="125" data-component-end-line="125" data-component-file="Card.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-foreground%22%7D" className="text-lg font-semibold text-foreground">{title}</h3>
          {description && (
            <p data-component-id="src/components/ui/Card.tsx:127:12" data-component-path="src/components/ui/Card.tsx" data-component-line="127" data-component-end-line="127" data-component-file="Card.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20mt-1%22%7D" className="text-sm text-muted-foreground mt-1">{description}</p>
          )}
        </div>
        {action && <div data-component-id="src/components/ui/Card.tsx:130:19" data-component-path="src/components/ui/Card.tsx" data-component-line="130" data-component-end-line="130" data-component-file="Card.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">{action}</div>}
      </div>
      {children}
    </Card>
  );
};

export default Card;
